/**
 * Theme selection.
 *
 * DARK IS THE DEFAULT; light is offered from the sidebar (Ben, 2026-08-29).
 *
 * Turning it on was NOT just adding the button. This file used to say the
 * light values were "complete in tokens.css" and they were not: eighteen
 * tokens existed only under [data-theme="dark"], so a switch would have left
 * them undefined — and an undefined var() with no fallback invalidates the
 * whole declaration silently. theme-parity.test.ts now fails on that.
 *
 * WHAT STAYS DARK IN BOTH THEMES, deliberately: the pitch palette and the
 * match page's own stylesheet (gms-dark.css, named for it). Those are
 * visualization surfaces whose overlays are tuned for contrast against a dark
 * field, and recolouring them is a design decision with a correctness
 * dimension — a player dot that stops being legible is a wrong answer, not a
 * preference.
 *
 * The attribute goes on <html> rather than <body> so it is set before first
 * paint and there is no flash of the wrong theme.
 */

export type Theme = 'dark' | 'light'

const STORAGE_KEY = 'priori.theme'
export const DEFAULT_THEME: Theme = 'dark'

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
  // Tells the browser which scrollbars and form controls to draw. Without it
  // native widgets render light on a dark page.
  document.documentElement.style.colorScheme = theme
}

/* Storage access is guarded because it genuinely throws in the wild: a browser
   with cookies blocked, or Safari's old private mode, raises on read. A theme
   preference is not worth taking the app down for, so failure falls back to
   the default rather than propagating. */
function readStorage(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value)
  } catch {
    /* preference not persisted; the applied theme still takes effect */
  }
}

export function storedTheme(): Theme {
  const raw = readStorage(STORAGE_KEY)
  return raw === 'light' || raw === 'dark' ? raw : DEFAULT_THEME
}

export function setTheme(theme: Theme): void {
  writeStorage(STORAGE_KEY, theme)
  applyTheme(theme)
}

/** Called once, before React renders. */
export function initTheme(): Theme {
  const theme = storedTheme()
  applyTheme(theme)
  return theme
}
